import { Dispatch, Action, ActionCreator } from "redux";
import { ThunkAction } from "redux-thunk";

import { DefaultErrorMessage } from "@/Contracts";
import {
	Product,
	ProductsRequest,
	AddOrEditProductRequest,
	ProductCount,
	ProductsResponse,
	MergeDuplicateProductsRequest
} from "@/Contracts/Products";
import {
	CustomViewsMetadata,
	CustomViewResponse,
	CustomView,
	ListPagePostRequest,
	BulkDeleteRecordsRequest,
	BulkUpdateRecordsRequest
} from "@/Contracts/UI";

import {
	getProducts,
	getProductDetails,
	deleteProduct,
	addProduct,
	editProduct,
	createProductView,
	deleteProductView,
	getAllProductViews,
	getProductListPost,
	getProductViewMetadata,
	updateProductView,
	mergeDuplicateProducts
} from "@/Services/Products";

import { GlobalState } from "@/Reducers/interface";
import { bulkDeleteRecords, bulkUpdateRecords } from "@/Services/Settings";
import { areFiltersChanged } from "@/Utils/common";

export enum ProductsActionTypes {
	SHOW_PRODUCTS_LOADER = "SHOW_PRODUCTS_LOADER",
	HIDE_PRODUCTS_LOADER = "HIDE_PRODUCTS_LOADER",
	PRODUCTS_REQUESTED = "PRODUCTS_REQUESTED",
	PRODUCTS_SUCCESSFUL = "PRODUCTS_SUCCESSFUL",
	PRODUCTS_FAILED = "PRODUCTS_FAILED",
	PRODUCTS_RESET = "PRODUCTS_RESET",
	PRODUCT_DETAILS_REQUESTED = "PRODUCT_DETAILS_REQUESTED",
	PRODUCT_DETAILS_SUCCESSFUL = "PRODUCT_DETAILS_SUCCESSFUL",
	PRODUCT_DETAILS_FAILED = "PRODUCT_DETAILS_FAILED",
	PRODUCT_DETAILS_UPDATED = "PRODUCT_DETAILS_UPDATED",
	PRODUCT_DETAILS_RESET = "PRODUCT_DETAILS_RESET",
	DELETE_PRODUCT_REQUESTED = "DELETE_PRODUCT_REQUESTED",
	DELETE_PRODUCT_SUCCESSFUL = "DELETE_PRODUCT_SUCCESSFUL",
	DELETE_PRODUCT_FAILED = "DELETE_PRODUCT_FAILED",
	ADD_PRODUCT_REQUESTED = "ADD_PRODUCT_REQUESTED",
	ADD_PRODUCT_SUCCESSFUL = "ADD_PRODUCT_SUCCESSFUL",
	ADD_PRODUCT_FAILED = "ADD_PRODUCT_FAILED",
	EDIT_PRODUCT_REQUESTED = "EDIT_PRODUCT_REQUESTED",
	EDIT_PRODUCT_SUCCESSFUL = "EDIT_PRODUCT_SUCCESSFUL",
	EDIT_PRODUCT_FAILED = "EDIT_PRODUCT_FAILED",
	ADD_PRODUCT_DRAWER_OPENED = "ADD_PRODUCT_DRAWER_OPENED",
	ADD_PRODUCT_DRAWER_CLOSED = "ADD_PRODUCT_DRAWER_CLOSED",
	EDIT_PRODUCT_DRAWER_OPENED = "EDIT_PRODUCT_DRAWER_OPENED",
	EDIT_PRODUCT_DRAWER_CLOSED = "EDIT_PRODUCT_DRAWER_CLOSED",
	ADD_ETILIZE_PRODUCT_DRAWER_OPENED = "ADD_ETILIZE_PRODUCT_DRAWER_OPENED",
	ADD_ETILIZE_PRODUCT_DRAWER_CLOSED = "ADD_ETILIZE_PRODUCT_DRAWER_CLOSED",
	SET_SELECTED_ETILIZE_PRODUCT = "SET_SELECTED_ETILIZE_PRODUCT",
	RESET_SELECTED_ETILIZE_PRODUCT = "RESET_SELECTED_ETILIZE_PRODUCT",
	CLONE_PRODUCT_REQUESTED = "CLONE_PRODUCT_REQUESTED",
	CLONE_PRODUCT_SUCCESSFUL = "CLONE_PRODUCT_SUCCESSFUL",
	CLONE_PRODUCT_FAILED = "CLONE_PRODUCT_FAILED",

	FETCH_ALL_PRODUCT_VIEWS_REQUESTED = "FETCH_ALL_PRODUCT_VIEWS_REQUESTED",
	FETCH_ALL_PRODUCT_VIEWS_SUCCESSFUL = "FETCH_ALL_PRODUCT_VIEWS_SUCCESSFUL",
	FETCH_ALL_PRODUCT_VIEWS_FAILED = "FETCH_ALL_PRODUCT_VIEWS_FAILED",

	FETCH_PRODUCT_VIEW_METADATA_REQUESTED = "FETCH_PRODUCT_VIEW_METADATA_REQUESTED",
	FETCH_PRODUCT_VIEW_METADATA_SUCCESSFUL = "FETCH_PRODUCT_VIEW_METADATA_SUCCESSFUL",
	FETCH_PRODUCT_VIEW_METADATA_FAILED = "FETCH_PRODUCT_VIEW_METADATA_FAILED",

	FETCH_PRODUCT_LIST_REQUESTED = "FETCH_PRODUCT_LIST_REQUESTED",
	FETCH_PRODUCT_LIST_SUCCESSFUL = "FETCH_PRODUCT_LIST_SUCCESSFUL",
	FETCH_PRODUCT_LIST_FAILED = "FETCH_PRODUCT_LIST_FAILED",

	UPDATE_PRODUCTS_CURRENT_ACTIVE_VIEW = "UPDATE_PRODUCTS_CURRENT_ACTIVE_VIEW",

	CREATE_PRODUCT_VIEW_REQUESTED = "CREATE_PRODUCT_VIEW_REQUESTED",
	CREATE_PRODUCT_VIEW_SUCCESSFUL = "CREATE_PRODUCT_VIEW_SUCCESSFUL",
	CREATE_PRODUCT_VIEW_FAILED = "CREATE_PRODUCT_VIEW_FAILED",

	UPDATE_PRODUCT_VIEW_REQUESTED = "UPDATE_PRODUCT_VIEW_REQUESTED",
	UPDATE_PRODUCT_VIEW_SUCCESSFUL = "UPDATE_PRODUCT_VIEW_SUCCESSFUL",
	UPDATE_PRODUCT_VIEW_FAILED = "UPDATE_PRODUCT_VIEW_FAILED",

	DELETE_PRODUCT_VIEW_REQUESTED = "DELETE_PRODUCT_VIEW_REQUESTED",
	DELETE_PRODUCT_VIEW_SUCCESSFUL = "DELETE_PRODUCT_VIEW_SUCCESSFUL",
	DELETE_PRODUCT_VIEW_FAILED = "DELETE_PRODUCT_VIEW_FAILED",

	BULK_ACTION_RECORDS_SELECTED = "BULK_ACTION_RECORDS_SELECTED",
	BULK_ACTION_RECORDS_RESET = "BULK_ACTION_RECORDS_RESET",

	BULK_DELETE_RECORDS_REQUESTED = "BULK_DELETE_RECORDS_REQUESTED",
	BULK_DELETE_RECORDS_SUCCESSFUL = "BULK_DELETE_RECORDS_SUCCESSFUL",
	BULK_DELETE_RECORDS_FAILED = "BULK_DELETE_RECORDS_FAILED",

	BULK_UPDATE_RECORDS_REQUESTED = "BULK_UPDATE_RECORDS_REQUESTED",
	BULK_UPDATE_RECORDS_SUCCESSFUL = "BULK_UPDATE_RECORDS_SUCCESSFUL",
	BULK_UPDATE_RECORDS_FAILED = "BULK_UPDATE_RECORDS_FAILED",
	RESET_PRODUCTS_CURRENT_ACTIVE_VIEW = "RESET_PRODUCTS_CURRENT_ACTIVE_VIEW",

	MERGE_DUPLICATE_PRODUCTS_REQUESTED = "MERGE_DUPLICATE_PRODUCTS_REQUESTED",
	MERGE_DUPLICATE_PRODUCTS_SUCCESSFUL = "MERGE_DUPLICATE_PRODUCTS_SUCCESSFUL",
	MERGE_DUPLICATE_PRODUCTS_FAILED = "MERGE_DUPLICATE_PRODUCTS_FAILED"
}

export type ShowProductsLoaderAction =
	Action<ProductsActionTypes.SHOW_PRODUCTS_LOADER>;
export type HideProductsLoaderAction =
	Action<ProductsActionTypes.HIDE_PRODUCTS_LOADER>;
export type ProductsRequestedAction =
	Action<ProductsActionTypes.PRODUCTS_REQUESTED>;
export interface ProductsSuccessfulAction
	extends Action<ProductsActionTypes.PRODUCTS_SUCCESSFUL> {
	readonly productList: Product[];
	readonly productCount: ProductCount;
}
export type ProductsFailedAction = Action<ProductsActionTypes.PRODUCTS_FAILED>;
export type ProductsResetAction = Action<ProductsActionTypes.PRODUCTS_RESET>;
export type ProductDetailsRequestedAction =
	Action<ProductsActionTypes.PRODUCT_DETAILS_REQUESTED>;
export interface ProductDetailsSuccessfulAction
	extends Action<ProductsActionTypes.PRODUCT_DETAILS_SUCCESSFUL> {
	readonly currentActiveProduct: Product;
}
export type ProductDetailsFailedAction =
	Action<ProductsActionTypes.PRODUCT_DETAILS_FAILED>;
export interface ProductDetailsUpdatedAction
	extends Action<ProductsActionTypes.PRODUCT_DETAILS_UPDATED> {
	readonly currentActiveProduct: Product;
}
export type ProductDetailsResetAction =
	Action<ProductsActionTypes.PRODUCT_DETAILS_RESET>;
export type DeleteProductRequestedAction =
	Action<ProductsActionTypes.DELETE_PRODUCT_REQUESTED>;
export interface DeleteProductSuccessfulAction
	extends Action<ProductsActionTypes.DELETE_PRODUCT_SUCCESSFUL> {
	readonly deleteProduct: Product;
}
export type DeleteProductFailedAction =
	Action<ProductsActionTypes.DELETE_PRODUCT_FAILED>;
export type AddProductRequestedAction =
	Action<ProductsActionTypes.ADD_PRODUCT_REQUESTED>;
export interface AddProductSuccessfulAction
	extends Action<ProductsActionTypes.ADD_PRODUCT_SUCCESSFUL> {
	readonly addProduct: Product;
}
export type AddProductFailedAction =
	Action<ProductsActionTypes.ADD_PRODUCT_FAILED>;
export type EditProductRequestedAction =
	Action<ProductsActionTypes.EDIT_PRODUCT_REQUESTED>;
export interface EditProductSuccessfulAction
	extends Action<ProductsActionTypes.EDIT_PRODUCT_SUCCESSFUL> {
	readonly editProduct: Product;
}
export type EditProductFailedAction =
	Action<ProductsActionTypes.EDIT_PRODUCT_FAILED>;
export type CloneProductRequestedAction =
	Action<ProductsActionTypes.CLONE_PRODUCT_REQUESTED>;
export interface CloneProductSuccessfulAction
	extends Action<ProductsActionTypes.CLONE_PRODUCT_SUCCESSFUL> {
	readonly cloneProduct: Product;
}
export type CloneProductFailedAction =
	Action<ProductsActionTypes.CLONE_PRODUCT_FAILED>;
export type AddProductDrawerOpenedAction =
	Action<ProductsActionTypes.ADD_PRODUCT_DRAWER_OPENED>;
export type AddProductDrawerClosedAction =
	Action<ProductsActionTypes.ADD_PRODUCT_DRAWER_CLOSED>;
export type EditProductDrawerOpenedAction =
	Action<ProductsActionTypes.EDIT_PRODUCT_DRAWER_OPENED>;
export type EditProductDrawerClosedAction =
	Action<ProductsActionTypes.EDIT_PRODUCT_DRAWER_CLOSED>;
export type AddEtilizeProductDrawerOpenedAction =
	Action<ProductsActionTypes.ADD_ETILIZE_PRODUCT_DRAWER_OPENED>;
export type AddEtilizeProductDrawerClosedAction =
	Action<ProductsActionTypes.ADD_ETILIZE_PRODUCT_DRAWER_CLOSED>;

export interface SetSelectedEtilizeProductAction
	extends Action<ProductsActionTypes.SET_SELECTED_ETILIZE_PRODUCT> {
	readonly selectedProduct: Product;
}
export type ResetSelectedEtilizeProductAction =
	Action<ProductsActionTypes.RESET_SELECTED_ETILIZE_PRODUCT>;

export type FetchProductViewMetadataRequested =
	Action<ProductsActionTypes.FETCH_PRODUCT_VIEW_METADATA_REQUESTED>;
export interface FetchProductViewMetadataSuccessful
	extends Action<ProductsActionTypes.FETCH_PRODUCT_VIEW_METADATA_SUCCESSFUL> {
	readonly metadata: CustomViewsMetadata;
}
export type FetchProductViewMetadataFailed =
	Action<ProductsActionTypes.FETCH_PRODUCT_VIEW_METADATA_FAILED>;

export type FetchAllProductViewRequested =
	Action<ProductsActionTypes.FETCH_ALL_PRODUCT_VIEWS_REQUESTED>;
export interface FetchAllProductViewSuccessful
	extends Action<ProductsActionTypes.FETCH_ALL_PRODUCT_VIEWS_SUCCESSFUL> {
	readonly viewList: CustomViewResponse;
}
export type FetchAllProductViewFailed =
	Action<ProductsActionTypes.FETCH_ALL_PRODUCT_VIEWS_FAILED>;

export type FetchProductListRequested =
	Action<ProductsActionTypes.FETCH_PRODUCT_LIST_REQUESTED>;
export interface FetchProductListSuccessful
	extends Action<ProductsActionTypes.FETCH_PRODUCT_LIST_SUCCESSFUL> {
	readonly productList: ProductsResponse;
}
export type FetchProductListFailed =
	Action<ProductsActionTypes.FETCH_PRODUCT_LIST_FAILED>;

export interface UpdateCurrentActiveViewAction
	extends Action<ProductsActionTypes.UPDATE_PRODUCTS_CURRENT_ACTIVE_VIEW> {
	readonly currentActiveView: CustomView;
	readonly isViewChanged: boolean;
}

export type CreateProductViewRequestedAction =
	Action<ProductsActionTypes.CREATE_PRODUCT_VIEW_REQUESTED>;
export interface CreateProductViewSuccessfulAction
	extends Action<ProductsActionTypes.CREATE_PRODUCT_VIEW_SUCCESSFUL> {
	readonly view: CustomView;
}
export type CreateProductViewFailedAction =
	Action<ProductsActionTypes.CREATE_PRODUCT_VIEW_FAILED>;

export type UpdateProductViewRequestedAction =
	Action<ProductsActionTypes.UPDATE_PRODUCT_VIEW_REQUESTED>;
export interface UpdateProductViewSuccessfulAction
	extends Action<ProductsActionTypes.UPDATE_PRODUCT_VIEW_SUCCESSFUL> {
	readonly view: CustomView;
}
export type UpdateProductViewFailedAction =
	Action<ProductsActionTypes.UPDATE_PRODUCT_VIEW_FAILED>;

export type DeleteProductViewRequestedAction =
	Action<ProductsActionTypes.DELETE_PRODUCT_VIEW_REQUESTED>;
export interface DeleteProductViewSuccessfulAction
	extends Action<ProductsActionTypes.DELETE_PRODUCT_VIEW_SUCCESSFUL> {
	readonly deletedView: CustomView;
	readonly initialView: CustomView;
}
export type DeleteProductViewFailedAction =
	Action<ProductsActionTypes.DELETE_PRODUCT_VIEW_FAILED>;

export type ResetCurrentActiveViewAction =
	Action<ProductsActionTypes.RESET_PRODUCTS_CURRENT_ACTIVE_VIEW>;

export type BulkActionRecordsResetAction =
	Action<ProductsActionTypes.BULK_ACTION_RECORDS_RESET>;
export type BulkActionRecordsSelectedAction =
	Action<ProductsActionTypes.BULK_ACTION_RECORDS_SELECTED>;
export type BulkDeleteRecordsRequestedAction =
	Action<ProductsActionTypes.BULK_DELETE_RECORDS_REQUESTED>;
export type BulkDeleteRecordsSuccessfulAction =
	Action<ProductsActionTypes.BULK_DELETE_RECORDS_SUCCESSFUL>;
export type BulkDeleteRecordsFailedAction =
	Action<ProductsActionTypes.BULK_DELETE_RECORDS_FAILED>;

export type BulkUpdateRecordsRequestedAction =
	Action<ProductsActionTypes.BULK_UPDATE_RECORDS_REQUESTED>;
export type BulkUpdateRecordsSuccessfulAction =
	Action<ProductsActionTypes.BULK_UPDATE_RECORDS_SUCCESSFUL>;
export type BulkUpdateRecordsFailedAction =
	Action<ProductsActionTypes.BULK_UPDATE_RECORDS_FAILED>;

export type MergeDuplicateProductsRequestedAction =
	Action<ProductsActionTypes.MERGE_DUPLICATE_PRODUCTS_REQUESTED>;
export interface MergeDuplicateProductsSuccessfulAction
	extends Action<ProductsActionTypes.MERGE_DUPLICATE_PRODUCTS_SUCCESSFUL> {
	readonly currentActiveProduct: Product;
}
export type MergeDuplicateProductsFailedAction =
	Action<ProductsActionTypes.MERGE_DUPLICATE_PRODUCTS_FAILED>;

export type ProductsActions =
	| ShowProductsLoaderAction
	| HideProductsLoaderAction
	| ProductsRequestedAction
	| ProductsSuccessfulAction
	| ProductsFailedAction
	| ProductDetailsUpdatedAction
	| ProductsResetAction
	| ProductDetailsRequestedAction
	| ProductDetailsSuccessfulAction
	| ProductDetailsFailedAction
	| ProductDetailsResetAction
	| DeleteProductRequestedAction
	| DeleteProductSuccessfulAction
	| DeleteProductFailedAction
	| AddProductRequestedAction
	| AddProductSuccessfulAction
	| AddProductFailedAction
	| EditProductRequestedAction
	| EditProductSuccessfulAction
	| EditProductFailedAction
	| AddProductDrawerOpenedAction
	| AddProductDrawerClosedAction
	| EditProductDrawerOpenedAction
	| EditProductDrawerClosedAction
	| AddEtilizeProductDrawerOpenedAction
	| AddEtilizeProductDrawerClosedAction
	| SetSelectedEtilizeProductAction
	| ResetSelectedEtilizeProductAction
	| CloneProductRequestedAction
	| CloneProductSuccessfulAction
	| CloneProductFailedAction
	| FetchProductViewMetadataRequested
	| FetchProductViewMetadataSuccessful
	| FetchProductViewMetadataFailed
	| FetchAllProductViewRequested
	| FetchAllProductViewSuccessful
	| FetchAllProductViewFailed
	| FetchProductListRequested
	| FetchProductListSuccessful
	| FetchProductListFailed
	| UpdateCurrentActiveViewAction
	| CreateProductViewRequestedAction
	| CreateProductViewSuccessfulAction
	| CreateProductViewFailedAction
	| UpdateProductViewRequestedAction
	| UpdateProductViewSuccessfulAction
	| UpdateProductViewFailedAction
	| DeleteProductViewRequestedAction
	| DeleteProductViewSuccessfulAction
	| DeleteProductViewFailedAction
	| ResetCurrentActiveViewAction
	| BulkActionRecordsSelectedAction
	| BulkActionRecordsResetAction
	| BulkDeleteRecordsRequestedAction
	| BulkDeleteRecordsSuccessfulAction
	| BulkDeleteRecordsFailedAction
	| BulkUpdateRecordsRequestedAction
	| BulkUpdateRecordsSuccessfulAction
	| BulkUpdateRecordsFailedAction
	| MergeDuplicateProductsRequestedAction
	| MergeDuplicateProductsSuccessfulAction
	| MergeDuplicateProductsFailedAction;

export interface ProductsDispatch {
	showProductsLoader: ActionCreator<
		ThunkAction<
			ShowProductsLoaderAction,
			GlobalState,
			null,
			ShowProductsLoaderAction
		>
	>;
	hideProductsLoader: ActionCreator<
		ThunkAction<
			HideProductsLoaderAction,
			GlobalState,
			null,
			HideProductsLoaderAction
		>
	>;
	getProducts: ActionCreator<
		ThunkAction<
			Promise<ProductsSuccessfulAction>,
			GlobalState,
			ProductsRequest,
			ProductsSuccessfulAction
		>
	>;
	resetProducts: ActionCreator<
		ThunkAction<ProductsResetAction, GlobalState, null, ProductsResetAction>
	>;
	getProductDetails: ActionCreator<
		ThunkAction<
			Promise<ProductDetailsSuccessfulAction>,
			GlobalState,
			string,
			ProductDetailsSuccessfulAction
		>
	>;
	updateProductDetails: ActionCreator<
		ThunkAction<
			ProductDetailsUpdatedAction,
			GlobalState,
			Product,
			ProductDetailsUpdatedAction
		>
	>;
	resetProductDetails: ActionCreator<
		ThunkAction<
			ProductDetailsResetAction,
			GlobalState,
			null,
			ProductDetailsResetAction
		>
	>;
	deleteProduct: ActionCreator<
		ThunkAction<
			Promise<DeleteProductSuccessfulAction>,
			GlobalState,
			Product,
			DeleteProductSuccessfulAction
		>
	>;
	addProduct: ActionCreator<
		ThunkAction<
			Promise<AddProductSuccessfulAction>,
			GlobalState,
			AddOrEditProductRequest,
			AddProductSuccessfulAction
		>
	>;
	editProduct: ActionCreator<
		ThunkAction<
			Promise<EditProductSuccessfulAction>,
			GlobalState,
			Product,
			EditProductSuccessfulAction
		>
	>;

	cloneProduct: ActionCreator<
		ThunkAction<
			Promise<CloneProductSuccessfulAction>,
			GlobalState,
			AddOrEditProductRequest,
			CloneProductSuccessfulAction
		>
	>;
	addProductDrawerOpened: ActionCreator<
		ThunkAction<
			AddProductDrawerOpenedAction,
			GlobalState,
			null,
			AddProductDrawerOpenedAction
		>
	>;
	addProductDrawerClosed: ActionCreator<
		ThunkAction<
			AddProductDrawerClosedAction,
			GlobalState,
			null,
			AddProductDrawerClosedAction
		>
	>;
	editProductDrawerOpened: ActionCreator<
		ThunkAction<
			EditProductDrawerOpenedAction,
			GlobalState,
			null,
			EditProductDrawerOpenedAction
		>
	>;
	editProductDrawerClosed: ActionCreator<
		ThunkAction<
			EditProductDrawerClosedAction,
			GlobalState,
			null,
			EditProductDrawerClosedAction
		>
	>;
	addEtilizeProductDrawerOpened: ActionCreator<
		ThunkAction<
			AddEtilizeProductDrawerOpenedAction,
			GlobalState,
			null,
			AddEtilizeProductDrawerOpenedAction
		>
	>;
	addEtilizeProductDrawerClosed: ActionCreator<
		ThunkAction<
			AddEtilizeProductDrawerClosedAction,
			GlobalState,
			null,
			AddEtilizeProductDrawerClosedAction
		>
	>;
	setSelectedEtilizeProduct: ActionCreator<
		ThunkAction<
			SetSelectedEtilizeProductAction,
			GlobalState,
			Product,
			SetSelectedEtilizeProductAction
		>
	>;
	resetSelectedEtilizeProduct: ActionCreator<
		ThunkAction<
			ResetSelectedEtilizeProductAction,
			GlobalState,
			null,
			ResetSelectedEtilizeProductAction
		>
	>;
	fetchProductViewMetadata: ActionCreator<
		ThunkAction<
			Promise<FetchProductViewMetadataSuccessful>,
			GlobalState,
			null,
			FetchProductViewMetadataSuccessful
		>
	>;
	fetchAllProductView: ActionCreator<
		ThunkAction<
			Promise<FetchAllProductViewSuccessful>,
			GlobalState,
			null,
			FetchAllProductViewSuccessful
		>
	>;
	fetchAllProducts: ActionCreator<
		ThunkAction<
			Promise<FetchProductListSuccessful>,
			GlobalState,
			ListPagePostRequest,
			FetchProductListSuccessful
		>
	>;
	updateCurrentActiveView: ActionCreator<
		ThunkAction<
			UpdateCurrentActiveViewAction,
			GlobalState,
			{ selectCurrentActiveView: CustomView; discardingView?: boolean },
			UpdateCurrentActiveViewAction
		>
	>;
	createProductView: ActionCreator<
		ThunkAction<
			Promise<CreateProductViewSuccessfulAction>,
			GlobalState,
			CustomView,
			CreateProductViewSuccessfulAction
		>
	>;
	updateProductView: ActionCreator<
		ThunkAction<
			Promise<UpdateProductViewSuccessfulAction>,
			GlobalState,
			CustomView,
			UpdateProductViewSuccessfulAction
		>
	>;
	deleteProductView: ActionCreator<
		ThunkAction<
			Promise<DeleteProductViewSuccessfulAction>,
			GlobalState,
			{ deleteView: CustomView; initialView: CustomView },
			DeleteProductViewSuccessfulAction
		>
	>;
	resetViews: ActionCreator<
		ThunkAction<
			ResetCurrentActiveViewAction,
			GlobalState,
			null,
			ResetCurrentActiveViewAction
		>
	>;
	bulkActionsRecordsSelected: ActionCreator<
		ThunkAction<
			BulkActionRecordsSelectedAction,
			GlobalState,
			null,
			BulkActionRecordsSelectedAction
		>
	>;
	bulkActionsRecordsReset: ActionCreator<
		ThunkAction<
			BulkActionRecordsResetAction,
			GlobalState,
			null,
			BulkActionRecordsResetAction
		>
	>;
	bulkDeleteRecords: ActionCreator<
		ThunkAction<
			Promise<BulkDeleteRecordsSuccessfulAction>,
			GlobalState,
			BulkDeleteRecordsRequest,
			BulkDeleteRecordsSuccessfulAction
		>
	>;
	bulkUpdateRecords: ActionCreator<
		ThunkAction<
			Promise<BulkUpdateRecordsSuccessfulAction>,
			GlobalState,
			BulkUpdateRecordsRequest,
			BulkUpdateRecordsSuccessfulAction
		>
	>;
	mergeDuplicateProducts: ActionCreator<
		ThunkAction<
			Promise<MergeDuplicateProductsSuccessfulAction>,
			GlobalState,
			MergeDuplicateProductsRequest,
			MergeDuplicateProductsSuccessfulAction
		>
	>;
}

export const ProductsActionCreators: ProductsDispatch = {
	showProductsLoader: () => {
		return (dispatch: Dispatch): ShowProductsLoaderAction => {
			const showProductsLoaderAction: ShowProductsLoaderAction = {
				type: ProductsActionTypes.SHOW_PRODUCTS_LOADER
			};
			return dispatch(showProductsLoaderAction);
		};
	},
	hideProductsLoader: () => {
		return (dispatch: Dispatch): HideProductsLoaderAction => {
			const hideProductsLoaderAction: HideProductsLoaderAction = {
				type: ProductsActionTypes.HIDE_PRODUCTS_LOADER
			};
			return dispatch(hideProductsLoaderAction);
		};
	},
	getProducts: (productsRequest: ProductsRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<ProductsSuccessfulAction> => {
			const productsRequestedAction: ProductsRequestedAction = {
				type: ProductsActionTypes.PRODUCTS_REQUESTED
			};
			dispatch(productsRequestedAction);
			try {
				const productsResponse = await getProducts(productsRequest);
				const productsSuccessfulAction: ProductsSuccessfulAction = {
					type: ProductsActionTypes.PRODUCTS_SUCCESSFUL,
					productList: productsResponse.data,
					productCount: productsResponse.filtered_count_map
				};
				return dispatch(productsSuccessfulAction);
			} catch (error) {
				const productsFailedAction: ProductsFailedAction = {
					type: ProductsActionTypes.PRODUCTS_FAILED
				};
				dispatch(productsFailedAction);

				return Promise.reject(error);
			}
		};
	},
	resetProducts: () => {
		return (dispatch: Dispatch): ProductsResetAction => {
			const productsResetAction: ProductsResetAction = {
				type: ProductsActionTypes.PRODUCTS_RESET
			};
			return dispatch(productsResetAction);
		};
	},
	getProductDetails: (productId: string, source?: string) => {
		return async (
			dispatch: Dispatch
		): Promise<ProductDetailsSuccessfulAction> => {
			const productDetailsRequestedAction: ProductDetailsRequestedAction =
				{
					type: ProductsActionTypes.PRODUCT_DETAILS_REQUESTED
				};
			dispatch(productDetailsRequestedAction);
			try {
				const productDetailsResponse = await getProductDetails(
					productId,
					source
				);
				const productDetailsSuccessfulAction: ProductDetailsSuccessfulAction =
					{
						type: ProductsActionTypes.PRODUCT_DETAILS_SUCCESSFUL,
						currentActiveProduct: productDetailsResponse
					};
				return dispatch(productDetailsSuccessfulAction);
			} catch (error) {
				const productDetailsFailedAction: ProductDetailsFailedAction = {
					type: ProductsActionTypes.PRODUCT_DETAILS_FAILED
				};
				dispatch(productDetailsFailedAction);

				return Promise.reject(error);
			}
		};
	},
	updateProductDetails: (currentActiveProduct: Product) => {
		return (dispatch: Dispatch): ProductDetailsUpdatedAction => {
			const productDetailsUpdatedAction: ProductDetailsUpdatedAction = {
				type: ProductsActionTypes.PRODUCT_DETAILS_UPDATED,
				currentActiveProduct
			};
			return dispatch(productDetailsUpdatedAction);
		};
	},
	resetProductDetails: () => {
		return (dispatch: Dispatch): ProductDetailsResetAction => {
			const productDetailsResetAction: ProductDetailsResetAction = {
				type: ProductsActionTypes.PRODUCT_DETAILS_RESET
			};
			return dispatch(productDetailsResetAction);
		};
	},
	deleteProduct: (deleteProductRequest: Product) => {
		return async (
			dispatch: Dispatch
		): Promise<DeleteProductSuccessfulAction> => {
			const deleteProductRequestedAction: DeleteProductRequestedAction = {
				type: ProductsActionTypes.DELETE_PRODUCT_REQUESTED
			};
			dispatch(deleteProductRequestedAction);
			try {
				const deleteProductResponse = await deleteProduct(
					deleteProductRequest
				);
				if (deleteProductResponse.deleted) {
					const deleteProductSuccessfulAction: DeleteProductSuccessfulAction =
						{
							type: ProductsActionTypes.DELETE_PRODUCT_SUCCESSFUL,
							deleteProduct: deleteProductRequest
						};
					return dispatch(deleteProductSuccessfulAction);
				} else {
					const deleteProductFailedAction: DeleteProductFailedAction =
						{
							type: ProductsActionTypes.DELETE_PRODUCT_FAILED
						};
					dispatch(deleteProductFailedAction);
					return Promise.reject(DefaultErrorMessage);
				}
			} catch (error) {
				const deleteProductFailedAction: DeleteProductFailedAction = {
					type: ProductsActionTypes.DELETE_PRODUCT_FAILED
				};
				dispatch(deleteProductFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addProduct: (addProductRequest: AddOrEditProductRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<AddProductSuccessfulAction> => {
			const addProductRequestedAction: AddProductRequestedAction = {
				type: ProductsActionTypes.ADD_PRODUCT_REQUESTED
			};
			dispatch(addProductRequestedAction);
			try {
				const addProductResponse = await addProduct(addProductRequest);
				const addProductSuccessfulAction: AddProductSuccessfulAction = {
					type: ProductsActionTypes.ADD_PRODUCT_SUCCESSFUL,
					addProduct: addProductResponse
				};
				return dispatch(addProductSuccessfulAction);
			} catch (error) {
				const addProductFailedAction: AddProductFailedAction = {
					type: ProductsActionTypes.ADD_PRODUCT_FAILED
				};
				dispatch(addProductFailedAction);

				return Promise.reject(error);
			}
		};
	},
	editProduct: (editProductRequest: AddOrEditProductRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<EditProductSuccessfulAction> => {
			const editProductRequestedAction: EditProductRequestedAction = {
				type: ProductsActionTypes.EDIT_PRODUCT_REQUESTED
			};
			dispatch(editProductRequestedAction);
			try {
				const editProductResponse = await editProduct(
					editProductRequest
				);
				const editProductSuccessfulAction: EditProductSuccessfulAction =
					{
						type: ProductsActionTypes.EDIT_PRODUCT_SUCCESSFUL,
						editProduct: editProductResponse
					};
				return dispatch(editProductSuccessfulAction);
			} catch (error) {
				const editProductFailedAction: EditProductFailedAction = {
					type: ProductsActionTypes.EDIT_PRODUCT_FAILED
				};
				dispatch(editProductFailedAction);

				return Promise.reject(error);
			}
		};
	},

	cloneProduct: (cloneProductRequest: AddOrEditProductRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<CloneProductSuccessfulAction> => {
			const cloneProductRequestedAction: CloneProductRequestedAction = {
				type: ProductsActionTypes.CLONE_PRODUCT_REQUESTED
			};
			dispatch(cloneProductRequestedAction);
			try {
				const addProductResponse = await addProduct(
					cloneProductRequest
				);
				const cloneProductSuccessfulAction: CloneProductSuccessfulAction =
					{
						type: ProductsActionTypes.CLONE_PRODUCT_SUCCESSFUL,
						cloneProduct: addProductResponse
					};
				return dispatch(cloneProductSuccessfulAction);
			} catch (error) {
				const cloneProductFailedAction: CloneProductFailedAction = {
					type: ProductsActionTypes.CLONE_PRODUCT_FAILED
				};
				dispatch(cloneProductFailedAction);

				return Promise.reject(error);
			}
		};
	},
	addProductDrawerOpened: () => {
		return (dispatch: Dispatch): AddProductDrawerOpenedAction => {
			const addProductDrawerOpenedAction: AddProductDrawerOpenedAction = {
				type: ProductsActionTypes.ADD_PRODUCT_DRAWER_OPENED
			};
			return dispatch(addProductDrawerOpenedAction);
		};
	},
	addProductDrawerClosed: () => {
		return (dispatch: Dispatch): AddProductDrawerClosedAction => {
			const addProductDrawerClosedAction: AddProductDrawerClosedAction = {
				type: ProductsActionTypes.ADD_PRODUCT_DRAWER_CLOSED
			};
			return dispatch(addProductDrawerClosedAction);
		};
	},
	editProductDrawerOpened: () => {
		return (dispatch: Dispatch): EditProductDrawerOpenedAction => {
			const editProductDrawerOpenedAction: EditProductDrawerOpenedAction =
				{
					type: ProductsActionTypes.EDIT_PRODUCT_DRAWER_OPENED
				};
			return dispatch(editProductDrawerOpenedAction);
		};
	},
	editProductDrawerClosed: () => {
		return (dispatch: Dispatch): EditProductDrawerClosedAction => {
			const editProductDrawerClosedAction: EditProductDrawerClosedAction =
				{
					type: ProductsActionTypes.EDIT_PRODUCT_DRAWER_CLOSED
				};
			return dispatch(editProductDrawerClosedAction);
		};
	},
	addEtilizeProductDrawerOpened: () => {
		return (dispatch: Dispatch): AddEtilizeProductDrawerOpenedAction => {
			const addEtilizeProductDrawerOpenedAction: AddEtilizeProductDrawerOpenedAction =
				{
					type: ProductsActionTypes.ADD_ETILIZE_PRODUCT_DRAWER_OPENED
				};
			return dispatch(addEtilizeProductDrawerOpenedAction);
		};
	},
	addEtilizeProductDrawerClosed: () => {
		return (dispatch: Dispatch): AddEtilizeProductDrawerClosedAction => {
			const addEtilizeProductDrawerClosedAction: AddEtilizeProductDrawerClosedAction =
				{
					type: ProductsActionTypes.ADD_ETILIZE_PRODUCT_DRAWER_CLOSED
				};
			return dispatch(addEtilizeProductDrawerClosedAction);
		};
	},
	setSelectedEtilizeProduct: (selectedProduct: Product) => {
		return (dispatch: Dispatch): SetSelectedEtilizeProductAction => {
			const setSelectedEtilizeProductAction: SetSelectedEtilizeProductAction =
				{
					type: ProductsActionTypes.SET_SELECTED_ETILIZE_PRODUCT,
					selectedProduct: selectedProduct
				};
			return dispatch(setSelectedEtilizeProductAction);
		};
	},
	resetSelectedEtilizeProduct: () => {
		return (dispatch: Dispatch): ResetSelectedEtilizeProductAction => {
			const resetSelectedEtilizeProductAction: ResetSelectedEtilizeProductAction =
				{
					type: ProductsActionTypes.RESET_SELECTED_ETILIZE_PRODUCT
				};
			return dispatch(resetSelectedEtilizeProductAction);
		};
	},
	fetchProductViewMetadata: () => {
		return async (
			dispatch: Dispatch
		): Promise<FetchProductViewMetadataSuccessful> => {
			const fetchViewMetadataRequested: FetchProductViewMetadataRequested =
				{
					type: ProductsActionTypes.FETCH_PRODUCT_VIEW_METADATA_REQUESTED
				};

			dispatch(fetchViewMetadataRequested);

			try {
				const response = await getProductViewMetadata();
				const fetchViewMetadataSuccessful: FetchProductViewMetadataSuccessful =
					{
						type: ProductsActionTypes.FETCH_PRODUCT_VIEW_METADATA_SUCCESSFUL,
						metadata: response
					};
				return dispatch(fetchViewMetadataSuccessful);
			} catch (error) {
				console.error(error);

				const fetchViewMetadataFailed: FetchProductViewMetadataFailed =
					{
						type: ProductsActionTypes.FETCH_PRODUCT_VIEW_METADATA_FAILED
					};
				dispatch(fetchViewMetadataFailed);

				return Promise.reject(error);
			}
		};
	},
	fetchAllProductView: () => {
		return async (
			dispatch: Dispatch
		): Promise<FetchAllProductViewSuccessful> => {
			const fetchAllProductViewRequested: FetchAllProductViewRequested = {
				type: ProductsActionTypes.FETCH_ALL_PRODUCT_VIEWS_REQUESTED
			};

			dispatch(fetchAllProductViewRequested);

			try {
				const response = await getAllProductViews();
				const fetchAllProductViewSuccessful: FetchAllProductViewSuccessful =
					{
						type: ProductsActionTypes.FETCH_ALL_PRODUCT_VIEWS_SUCCESSFUL,
						viewList: response
					};
				return dispatch(fetchAllProductViewSuccessful);
			} catch (error) {
				console.error(error);

				const fetchAllProductViewFailed: FetchAllProductViewFailed = {
					type: ProductsActionTypes.FETCH_ALL_PRODUCT_VIEWS_FAILED
				};
				dispatch(fetchAllProductViewFailed);

				return Promise.reject(error);
			}
		};
	},
	fetchAllProducts: (productRequest: ListPagePostRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<FetchProductListSuccessful> => {
			const fetchProductListRequested: FetchProductListRequested = {
				type: ProductsActionTypes.FETCH_PRODUCT_LIST_REQUESTED
			};
			dispatch(fetchProductListRequested);
			try {
				const response = await getProductListPost(productRequest);

				const fetchProductListSuccessful: FetchProductListSuccessful = {
					type: ProductsActionTypes.FETCH_PRODUCT_LIST_SUCCESSFUL,
					productList: response
				};

				return dispatch(fetchProductListSuccessful);
			} catch (error) {
				const fetchProductListFailed: FetchProductListFailed = {
					type: ProductsActionTypes.FETCH_PRODUCT_LIST_FAILED
				};

				dispatch(fetchProductListFailed);

				return Promise.reject(error);
			}
		};
	},
	updateCurrentActiveView: (
		selectedCurrentActiveView: CustomView,
		discardingView?: boolean
	) => {
		return (
			dispatch: Dispatch,
			getState: () => GlobalState
		): UpdateCurrentActiveViewAction => {
			const currentActiveView = getState().products.currentActiveView;
			const prevViewData = getState().products.viewList?.data.find(
				view => view.id === selectedCurrentActiveView?.id
			);

			const isSameView =
				currentActiveView?.id === selectedCurrentActiveView?.id;

			if (isSameView) {
				// if its a custom view then it would have an id
				if (prevViewData?.id) {
					const hasFiltersChanged = areFiltersChanged(
						prevViewData.filter_criteria,
						selectedCurrentActiveView.filter_criteria
					);

					const hasColumnsChanged =
						JSON.stringify(selectedCurrentActiveView.columns) !==
						JSON.stringify(prevViewData.columns);

					const updateCurrentActiveViewAction: UpdateCurrentActiveViewAction =
						{
							type: ProductsActionTypes.UPDATE_PRODUCTS_CURRENT_ACTIVE_VIEW,
							currentActiveView: selectedCurrentActiveView,
							isViewChanged: discardingView
								? false
								: hasFiltersChanged || hasColumnsChanged
						};
					return dispatch(updateCurrentActiveViewAction);
				}

				// if its a system view then no id

				if (currentActiveView) {
					const hasFiltersChanged = areFiltersChanged(
						currentActiveView.filter_criteria,
						selectedCurrentActiveView.filter_criteria
					);

					const hasColumnsChanged =
						JSON.stringify(selectedCurrentActiveView.columns) !==
						JSON.stringify(currentActiveView?.columns);

					const updateCurrentActiveViewAction: UpdateCurrentActiveViewAction =
						{
							type: ProductsActionTypes.UPDATE_PRODUCTS_CURRENT_ACTIVE_VIEW,
							currentActiveView: selectedCurrentActiveView,
							isViewChanged: discardingView
								? false
								: hasFiltersChanged || hasColumnsChanged
						};
					return dispatch(updateCurrentActiveViewAction);
				}
			}

			const updateCurrentActiveViewAction: UpdateCurrentActiveViewAction =
				{
					type: ProductsActionTypes.UPDATE_PRODUCTS_CURRENT_ACTIVE_VIEW,
					currentActiveView: selectedCurrentActiveView,
					isViewChanged: discardingView ? true : false
				};
			return dispatch(updateCurrentActiveViewAction);
		};
	},
	createProductView: (view: CustomView) => {
		return async (
			dispatch: Dispatch
		): Promise<CreateProductViewSuccessfulAction> => {
			const createProductViewRequestedAction: CreateProductViewRequestedAction =
				{
					type: ProductsActionTypes.CREATE_PRODUCT_VIEW_REQUESTED
				};

			dispatch(createProductViewRequestedAction);

			try {
				const response = await createProductView(view);
				const createProductViewSuccessfulAction: CreateProductViewSuccessfulAction =
					{
						type: ProductsActionTypes.CREATE_PRODUCT_VIEW_SUCCESSFUL,
						view: response
					};
				return dispatch(createProductViewSuccessfulAction);
			} catch (error) {
				const createProductViewFailedAction: CreateProductViewFailedAction =
					{
						type: ProductsActionTypes.CREATE_PRODUCT_VIEW_FAILED
					};
				dispatch(createProductViewFailedAction);

				return Promise.reject(error);
			}
		};
	},
	updateProductView: (view: CustomView) => {
		return async (
			dispatch: Dispatch
		): Promise<UpdateProductViewSuccessfulAction> => {
			const updateProductViewRequestedAction: UpdateProductViewRequestedAction =
				{
					type: ProductsActionTypes.UPDATE_PRODUCT_VIEW_REQUESTED
				};

			dispatch(updateProductViewRequestedAction);

			try {
				const response = await updateProductView(view);
				const updateProductViewSuccessfulAction: UpdateProductViewSuccessfulAction =
					{
						type: ProductsActionTypes.UPDATE_PRODUCT_VIEW_SUCCESSFUL,
						view: response
					};
				return dispatch(updateProductViewSuccessfulAction);
			} catch (error) {
				const updateProductViewFailedAction: UpdateProductViewFailedAction =
					{
						type: ProductsActionTypes.UPDATE_PRODUCT_VIEW_FAILED
					};
				dispatch(updateProductViewFailedAction);

				return Promise.reject(error);
			}
		};
	},
	deleteProductView: (deleteView: CustomView, initialView: CustomView) => {
		return async (
			dispatch: Dispatch
		): Promise<DeleteProductViewSuccessfulAction> => {
			const deleteProductViewRequestedAction: DeleteProductViewRequestedAction =
				{
					type: ProductsActionTypes.DELETE_PRODUCT_VIEW_REQUESTED
				};

			dispatch(deleteProductViewRequestedAction);

			try {
				await deleteProductView(deleteView.id);
				const deleteProductViewSuccessfulAction: DeleteProductViewSuccessfulAction =
					{
						type: ProductsActionTypes.DELETE_PRODUCT_VIEW_SUCCESSFUL,
						deletedView: deleteView,
						initialView: initialView
					};
				return dispatch(deleteProductViewSuccessfulAction);
			} catch (error) {
				const deleteProductViewFailedAction: DeleteProductViewFailedAction =
					{
						type: ProductsActionTypes.DELETE_PRODUCT_VIEW_FAILED
					};
				dispatch(deleteProductViewFailedAction);

				return Promise.reject(error);
			}
		};
	},
	resetViews: () => {
		return (dispatch: Dispatch): ResetCurrentActiveViewAction => {
			const resetCurrentActiveViewAction: ResetCurrentActiveViewAction = {
				type: ProductsActionTypes.RESET_PRODUCTS_CURRENT_ACTIVE_VIEW
			};

			return dispatch(resetCurrentActiveViewAction);
		};
	},
	bulkActionsRecordsSelected: () => {
		return (dispatch: Dispatch): BulkActionRecordsSelectedAction => {
			const bulkActionRecordsSelectedAction: BulkActionRecordsSelectedAction =
				{
					type: ProductsActionTypes.BULK_ACTION_RECORDS_SELECTED
				};

			return dispatch(bulkActionRecordsSelectedAction);
		};
	},
	bulkActionsRecordsReset: () => {
		return (dispatch: Dispatch): BulkActionRecordsResetAction => {
			const bulkActionRecordsResetAction: BulkActionRecordsResetAction = {
				type: ProductsActionTypes.BULK_ACTION_RECORDS_RESET
			};

			return dispatch(bulkActionRecordsResetAction);
		};
	},
	bulkDeleteRecords: (deleteRecordsRequest: BulkDeleteRecordsRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<BulkDeleteRecordsSuccessfulAction> => {
			const bulkDeleteRecordsRequestedAction: BulkDeleteRecordsRequestedAction =
				{
					type: ProductsActionTypes.BULK_DELETE_RECORDS_REQUESTED
				};

			dispatch(bulkDeleteRecordsRequestedAction);

			try {
				await bulkDeleteRecords(deleteRecordsRequest);

				const bulkDeleteRecordsSuccessfulAction: BulkDeleteRecordsSuccessfulAction =
					{
						type: ProductsActionTypes.BULK_DELETE_RECORDS_SUCCESSFUL
					};

				return dispatch(bulkDeleteRecordsSuccessfulAction);
			} catch (error) {
				const bulkDeleteRecordsFailedAction: BulkDeleteRecordsFailedAction =
					{
						type: ProductsActionTypes.BULK_DELETE_RECORDS_FAILED
					};
				dispatch(bulkDeleteRecordsFailedAction);

				return Promise.reject(error);
			}
		};
	},
	bulkUpdateRecords: (updateRecordsRequest: BulkUpdateRecordsRequest) => {
		return async (
			dispatch: Dispatch
		): Promise<BulkUpdateRecordsSuccessfulAction> => {
			const bulkUpdateRecordsRequestedAction: BulkUpdateRecordsRequestedAction =
				{
					type: ProductsActionTypes.BULK_UPDATE_RECORDS_REQUESTED
				};

			dispatch(bulkUpdateRecordsRequestedAction);

			try {
				await bulkUpdateRecords(updateRecordsRequest);

				const bulkUpdateRecordsSuccessfulAction: BulkUpdateRecordsSuccessfulAction =
					{
						type: ProductsActionTypes.BULK_UPDATE_RECORDS_SUCCESSFUL
					};

				return dispatch(bulkUpdateRecordsSuccessfulAction);
			} catch (error) {
				const bulkUpdateRecordsFailedAction: BulkUpdateRecordsFailedAction =
					{
						type: ProductsActionTypes.BULK_UPDATE_RECORDS_FAILED
					};
				dispatch(bulkUpdateRecordsFailedAction);

				return Promise.reject(error);
			}
		};
	},
	mergeDuplicateProducts: (
		productId: string,
		mergeDuplicateProductsRequest: MergeDuplicateProductsRequest
	) => {
		return async (
			dispatch: Dispatch
		): Promise<MergeDuplicateProductsSuccessfulAction> => {
			const mergeDuplicateProductsRequestedAction: MergeDuplicateProductsRequestedAction =
				{
					type: ProductsActionTypes.MERGE_DUPLICATE_PRODUCTS_REQUESTED
				};
			dispatch(mergeDuplicateProductsRequestedAction);
			try {
				const productDetailsResponse = await mergeDuplicateProducts(
					productId,
					mergeDuplicateProductsRequest
				);
				const mergeDuplicateProductsSuccessfulAction: MergeDuplicateProductsSuccessfulAction =
					{
						type: ProductsActionTypes.MERGE_DUPLICATE_PRODUCTS_SUCCESSFUL,
						currentActiveProduct: productDetailsResponse
					};
				return dispatch(mergeDuplicateProductsSuccessfulAction);
			} catch (error) {
				const mergeDuplicateProductsFailedAction: MergeDuplicateProductsFailedAction =
					{
						type: ProductsActionTypes.MERGE_DUPLICATE_PRODUCTS_FAILED
					};
				dispatch(mergeDuplicateProductsFailedAction);

				return Promise.reject(error);
			}
		};
	}
};

export default ProductsActionCreators;
